/**
 * API client for the Diagnostic Feedback System.
 *
 * Learner profile (macros + top-3 weaknesses per module, gated by cold-start) and the admin
 * authoring endpoints (tag dictionary, batch-tag, recommendation stats).
 *
 * @module diagnosticApi
 */

import { apiGet, apiPost } from "@/lib/apiUtils";

export type DiagnosticModule = "reading" | "listening" | "writing" | "speaking";

export interface DiagnosticResource {
  readonly title?: string;
  readonly id?: string;
  readonly min_cefr?: string;
}

export interface DiagnosticWeakness {
  readonly tag: string;
  readonly module: string;
  readonly error_count: number;
  readonly opportunity_count: number;
  readonly rate: number;
  readonly weakness_score: number;
  readonly confidence: "low" | "medium" | "high";
  readonly explain: string;
  readonly resource: DiagnosticResource;
}

export interface MacroCompetency {
  readonly macro: string;
  readonly error_count: number;
  readonly opportunity_count: number;
  readonly rate: number;
}

export interface ModuleProfile {
  readonly module: string;
  readonly attempted_items: number;
  readonly unlocked: boolean;
  readonly macros: MacroCompetency[];
  readonly weaknesses: DiagnosticWeakness[];
}

export interface DiagnosticProfile {
  readonly enabled: boolean;
  readonly required_items: number;
  readonly modules: ModuleProfile[];
}

/** Per-answer resolved feedback attached to the results-review answers payload. */
export interface DiagnosticItemFeedback {
  readonly tag: string;
  readonly explain: string;
  readonly resource: DiagnosticResource;
}

// ---------------------------------------------------------------------------
// Learner
// ---------------------------------------------------------------------------

export async function getDiagnosticProfile(): Promise<DiagnosticProfile> {
  const result = await apiGet<DiagnosticProfile>("/diagnostic/profile");
  return result ?? { enabled: false, required_items: 30, modules: [] };
}

// ---------------------------------------------------------------------------
// Admin
// ---------------------------------------------------------------------------

export interface DiagnosticTagsResponse {
  readonly level: string | null;
  readonly tags: Record<string, string>;
}

export async function getDiagnosticTags(
  level?: string,
): Promise<DiagnosticTagsResponse> {
  const query = level ? `?level=${encodeURIComponent(level)}` : "";
  const result = await apiGet<DiagnosticTagsResponse>(
    `/admin/diagnostic-tags${query}`,
  );
  return result ?? { level: level ?? null, tags: {} };
}

export interface BatchTagRequest {
  module: string;
  level?: string | null;
  exam_type?: string | null;
  limit?: number;
}

export interface BatchTagResponse {
  readonly queued: number;
  readonly message: string;
}

export async function startBatchTag(
  body: BatchTagRequest,
): Promise<BatchTagResponse> {
  const result = await apiPost<BatchTagResponse>(
    "/admin/diagnostic/batch-tag",
    body,
  );
  return result ?? { queued: 0, message: "" };
}

export interface RecommendationStat {
  readonly tag: string;
  readonly resource_id: string | null;
  readonly served_count: number;
  readonly avg_error_rate_before: number | null;
  readonly avg_error_rate_after: number | null;
  readonly delta: number | null;
}

export async function getRecommendationStats(): Promise<RecommendationStat[]> {
  const result = await apiGet<{ stats: RecommendationStat[] }>(
    "/admin/diagnostic/recommendation-stats",
  );
  return result?.stats ?? [];
}
